<template>
  <div class="category-filters" :class="platform" data-t="filters">
    <FavoriteButton
      v-if="isAuthenticated"
      v-model="favorite"
      class="favorite-button"
    />
    <StSelect
      v-if="themesList.length > 1"
      v-model="theme"
      :items="themesList"
      size="s"
      :placeholder="t('casino.themes')"
      :is-native="isMobile"
      class="filter"
      data-t="casino-themes-3buc"
    />
    <StSelect
      v-model="provider"
      :items="providersList"
      size="s"
      :placeholder="t('casino.providers')"
      :is-native="isMobile"
      class="filter"
      data-t="casino-providers-0jr1"
    />
    <slot />
  </div>
</template>

<script setup lang="ts">
import { useCasinoDeps } from '@st/casino/useDeps'
import FavoriteButton from './FavoriteButton.vue'

interface Props {
  parentCode: string
  subCategoryCode?: string
}
const { parentCode, subCategoryCode } = defineProps<Props>()
const { locale, t } = useI18n()
const { isMobile, platform } = usePlatform()
const { isAuthenticated } = useCasinoDeps()

const theme = defineModel<number | 'all' | undefined>('theme', {
  required: true,
})
const { data: themesList } = useStFetch('/casino/category/find', {
  method: 'post',
  body: computed(() => ({
    params: {
      parentCode,
      type: 'theme' as const,
      language: locale.value as any,
    },
    pagination: {
      page: 0,
      perPage: 50,
      orderBy: [{ fieldName: 'weight' as const, sortOrder: 'DESC' as const }],
    },
  })),
  transform: (response) => [
    { id: 'all', title: t('casino.all') },
    ...response.data.map((item) => ({
      id: item.id,
      title: item.name || item.code,
      code: item.code,
    })),
  ],
  default: () => [],
})

const categoryCode = computed(() =>
  subCategoryCode === 'all' ? parentCode : subCategoryCode || parentCode,
)

const provider = defineModel<string | undefined>('provider', { required: true })
const favorite = defineModel<boolean>('favorite')
const { data: providersList } = useStFetch('/casino/provider/find', {
  method: 'post',
  default: () => [],
  body: computed(() => ({
    params: {
      language: locale.value as any,
      categoryCode: categoryCode.value,
      ...(isMobile.value ? { forMobile: true } : { forDesktop: true }),
    },
    pagination: {
      page: 0,
      perPage: 50,
      orderBy: [{ fieldName: 'weight' as const, sortOrder: 'DESC' as const }],
    },
  })),
  transform: (response) => [
    { id: 'all', title: t('allProviders') },
    ...response.data.map((item) => ({
      id: item.code,
      title: item.name || item.code,
    })),
  ],
})

const filters = useSessionStorage<{
  sort: string
  categoryTheme: number | 'all' | undefined
  providerCode: string | undefined
  isFavorite: boolean
}>('casino-filters', {
  sort: 'weightInCategory',
  categoryTheme: undefined,
  providerCode: undefined,
  isFavorite: false,
})

function shouldResetValue(list: any[], currentValue: any): boolean {
  return Boolean(
    currentValue &&
      list?.length &&
      !list.find((item) => item.id === currentValue),
  )
}

watch(
  [providersList, provider],
  ([list, currentProvider]) => {
    if (shouldResetValue(list, currentProvider)) {
      provider.value = undefined
      delete filters.value.providerCode
    }
  },
  { immediate: true },
)

watch(
  [themesList, theme],
  ([list, currentTheme]) => {
    if (shouldResetValue(list, currentTheme)) {
      theme.value = undefined
      delete filters.value.categoryTheme
    }
  },
  { immediate: true },
)
</script>

<style scoped>
.filter {
  flex: 1 1 0;
  min-width: 0;
}

.category-filters {
  display: flex;
  flex-grow: 1;
  gap: var(--spacing-100);
  align-items: center;

  &.desktop {
    .filter {
      min-width: 148px;
    }
  }

  &.mobile {
    .favorite-button {
      order: 4;
    }
  }
}
</style>
